import { useEffect, useState } from "react";
import { fetchSeoMap } from "src/api/payload/seo";
import { isPayloadSource } from "src/utils/hubSource";

const seoMapCache = new Map();
const pendingRequests = new Map();

const normalizeLocale = (locale) => (locale === "fr" ? "fr" : "en");

function loadSeoMap(locale) {
  if (seoMapCache.has(locale)) {
    return Promise.resolve(seoMapCache.get(locale));
  }
  if (pendingRequests.has(locale)) {
    return pendingRequests.get(locale);
  }

  const request = fetchSeoMap(locale)
    .then((seoMap) => {
      seoMapCache.set(locale, seoMap);
      return seoMap;
    })
    .finally(() => pendingRequests.delete(locale));

  pendingRequests.set(locale, request);
  return request;
}

export function usePayloadSeoMap(locale) {
  const enabled = isPayloadSource("seo");
  const normalizedLocale = normalizeLocale(locale);
  const [seoMap, setSeoMap] = useState(
    () => seoMapCache.get(normalizedLocale) || null
  );

  useEffect(() => {
    if (!enabled) {
      setSeoMap(null);
      return;
    }

    const cachedMap = seoMapCache.get(normalizedLocale);
    if (cachedMap) {
      setSeoMap(cachedMap);
      return;
    }

    let cancelled = false;
    setSeoMap(null);
    loadSeoMap(normalizedLocale)
      .then((nextSeoMap) => {
        if (!cancelled) setSeoMap(nextSeoMap);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error fetching SEO map:", error);
          setSeoMap(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, normalizedLocale]);

  return seoMap;
}